import { CustomBanner } from '@/components';
import { AccountPlan } from '@/constants/enum';
import { handleSendMoreShipmentMessage } from '@/helpers';
import { useUpgradeTracking } from '@/hooks/useUpgradeTracking';
import dashboardPageSlice, { closedUsageBannerStageSelector } from '@/redux/features/dashboard.slice';
import { accountSelector } from '@/redux/features/plan.slice';
import { IUserPlan } from '@/types/dashboard';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface IProps {
  billingSummary: IUserPlan['billingSummary'];
  totalQuota: number | string;
}

const money = (value?: number) => `$${(value ?? 0).toFixed(2)}`;

const UsageQuotaBanner = ({ billingSummary, totalQuota }: IProps) => {
  const dispatch = useDispatch();
  const { navigateToPlan } = useUpgradeTracking();
  const { plan } = useSelector(accountSelector);
  const closedStage = useSelector(closedUsageBannerStageSelector);
  const stage = billingSummary.usageBannerStage ?? 'NONE';
  const isEnterprise = plan === AccountPlan.Enterprise;

  useEffect(() => {
    if (stage === 'NONE' && closedStage) dispatch(dashboardPageSlice.actions.handleCloseUsageBanner(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, closedStage]);

  if (stage === 'NONE') return null;

  const cap = money(billingSummary.usageCappedAmount);
  const used = money(billingSummary.usageBalanceUsed);
  const isDismissible = stage !== 'CRITICAL';
  const isReached = (billingSummary.usageCapPercent ?? 0) >= 100;

  const handleUpgradeNow = () => {
    navigateToPlan({ trigger_type: 'banner', trigger_element: 'Upgrade now', feature: 'quota_limit' });
  };

  const upgradeAction = {
    content: isEnterprise ? 'Discuss for more shipments volumn' : 'Upgrade now',
    onAction: isEnterprise ? handleSendMoreShipmentMessage : handleUpgradeNow,
  };

  const content = {
    INFO: {
      tone: 'info' as const,
      title: 'Extra shipments are now billed automatically',
      body: `You've used all ${totalQuota} included shipments this period. Extra shipments are now billed at ${money(
        billingSummary.usagePricePerTracking,
      )} each, up to your plan's ${cap} limit.`,
      action: undefined,
    },
    WARNING: {
      tone: 'warning' as const,
      title: "You've used 80% of your overage spending limit",
      body: `You've used ${used} of your ${cap} overage limit this cycle. Upgrade your plan to raise your limit and avoid interrupted tracking.`,
      action: upgradeAction,
    },
    CRITICAL: {
      tone: 'critical' as const,
      title: isReached ? "You've reached your overage spending limit" : "You've nearly reached your overage spending limit",
      body: `You've used ${used} of your ${cap} overage limit this cycle. New shipments beyond it won't be tracked until next cycle or until you upgrade.`,
      action: upgradeAction,
    },
  }[stage];

  return (
    <CustomBanner
      maxHeight="15rem"
      isClose={isDismissible && closedStage === stage}
      tone={content.tone}
      title={content.title}
      action={content.action}
      onDismiss={isDismissible ? () => dispatch(dashboardPageSlice.actions.handleCloseUsageBanner(stage)) : undefined}
    >
      {content.body}
    </CustomBanner>
  );
};

export default UsageQuotaBanner;
